import { useEffect, useRef, type ReactNode } from 'react'
import type { ComponentName, Host, HostCommand, HostError, InstallInfo, SwitchResult, SwitchStep } from '../../api'
import { t, type Key } from '../../i18n'
import { Icon, Toast, type IconName } from '../../ui'
import { pushOperatorEvent } from './operatorEvents'

/*
 * Restart node, Update logic and Update agent (HOLD-A8, node-holders §8). The host
 * agent runs the switch; the center passes its result as it came. Here: what the
 * console allows on a host (layout, caps, what the release changes, publishers on
 * the node), and the result in words — steps, "Restarted on #N…", "Rolled back at
 * verify: the old logic serves", a failed rollback as an error. A pause is shown
 * only when the agent sent pause_ms. Texts: i18n `node.*` (EN / ES / RU).
 */

export type SwitchService = 'restart' | 'logic' | 'agent'

/** Numbers, versions, names and generations: JetBrains Mono, tabular. */
export const Num = ({ children }: { children: ReactNode }) => <span className="font-mono text-text tabular-nums slashed-zero">{children}</span>

/** A text of the dictionary with its parameters in mono (the node name, #N, counts, versions). */
export function tn(key: Key, vars: Record<string, string | number> = {}): ReactNode {
  const tpl = t(key)
  const out: ReactNode[] = []
  let last = 0
  for (const m of tpl.matchAll(/\{(\w+)\}/g)) {
    out.push(tpl.slice(last, m.index))
    const v = vars[m[1]!]
    out.push(v === undefined ? m[0] : <Num key={`${m.index}`}>{String(v)}</Num>)
    last = m.index! + m[0].length
  }
  out.push(tpl.slice(last))
  return <>{out}</>
}

/** The singular for one, the plural otherwise. */
export const one = (n: number, k1: Key, kn: Key) => (n === 1 ? k1 : kn)

/** The A7 / A8 code an error starts with ("verify_failed: …"). */
export function codeOf(text?: string): string {
  const m = /^([a-z_]+):/.exec(text ?? '')
  return m ? m[1]! : ''
}

const CODE_KEYS = new Set([
  'busy', 'node_not_split', 'holders_unhealthy', 'color_mismatch', 'holders_changed', 'holders_must_change', 'image', 'start_failed', 'ready_timeout',
  'promote_failed', 'stale_generation', 'retire_failed', 'center_timeout', 'verify_failed', 'rollback_failed', 'needs_deploy', 'up_to_date',
  'changes_unknown', 'no_components', 'publishers_present', 'self_update_off', 'offline',
])

/** A code in words; an unknown code stays as the center sent it. */
export function codeText(code: string, raw?: string): string {
  if (CODE_KEYS.has(code)) return t(`node.code.${code}` as Key)
  return raw || code
}

/** The center's refusal (an HTTP error with a code) in words. */
export function refusalText(h: HostError | undefined): string | undefined {
  if (!h?.code) return undefined
  if (CODE_KEYS.has(h.code)) return codeText(h.code)
  return undefined
}

const STEP_KEYS = new Set(['checks', 'start', 'promote', 'verify', 'finish', 'rollback'])
export const stepName = (s: string) => (STEP_KEYS.has(s) ? t(`node.step.${s}` as Key) : s)

export const COMPONENTS: ComponentName[] = ['logic', 'ingress', 'egress', 'netns', 'caddy', 'agent']

export const has = (h: Host | null | undefined, ...caps: string[]) => !!h && caps.every((c) => h.caps?.includes(c))

/** The version a component of the center's release has (absent: not named). */
export function releaseVersion(info: InstallInfo | undefined, c: ComponentName): string | undefined {
  const r = info?.components?.[c]
  if (!r) return undefined
  if (r.version) return r.version
  return r.image ? r.image.split('@')[0]!.split(':').pop() : undefined
}

/** Why an action is off on this host. Empty means the operator can open the dialog. */
export function switchBlock(service: SwitchService, host: Host | null | undefined, info?: InstallInfo): string {
  if (!host) return ''
  if (service !== 'agent' && host.layout === 'monolith') return t('node.why.monolith')
  if (service === 'restart') return ''
  if (!has(host, 'release_schema_2', service === 'agent' ? 'update_agent' : 'update_logic')) return t('node.why.noCaps')
  if (!info?.components) return t('node.why.noComponents')
  if (!host.changes) return t('node.why.unknown')
  if (service === 'logic') {
    if (host.changes.some((c) => c !== 'logic' && c !== 'agent')) return t('node.why.holdersChange')
    if (!host.changes.includes('logic')) return t('node.why.logicSame', { version: host.components?.logic?.version ?? releaseVersion(info, 'logic') ?? '—' })
  } else if (!host.changes.includes('agent')) {
    return t('node.why.agentSame', { version: host.components?.agent?.version ?? host.agent_version ?? '—' })
  }
  return ''
}

/** Holders, Caddy, the OS and a reboot replace what the encoders talk to: off while a split node has publishers. */
export function freeFirst(host: Host | null | undefined, publishers: number): string {
  return host?.layout === 'split' && publishers > 0 ? t('node.why.free') : ''
}

export type Outcome = { state: 'live' | 'warn' | 'fail'; text: string }

/** The result of a switch in one sentence, its state, and the pause when it was measured. */
export function outcome(service: SwitchService, c: Pick<HostCommand, 'state' | 'error' | 'data'>, ctx: { name: string; release?: number; streams: number; version?: string }): Outcome {
  const d = (c.data ?? {}) as SwitchResult
  const k = typeof d.streams === 'number' ? d.streams : ctx.streams
  const pause = typeof d.pause_ms === 'number' ? ` ${t('node.pause', { n: d.pause_ms })}` : ''
  if (c.state === 'ok') {
    if (service === 'agent') {
      const v = d.version || ctx.version || '—'
      return { state: 'live', text: d.agent_update === false ? t('node.done.agentSame', { version: v }) : t('node.done.agent', { version: v }) }
    }
    if (service === 'logic') {
      const v = d.logic_version || ctx.version || '—'
      return { state: 'live', text: t(one(k, 'node.done.logic.one', 'node.done.logic.many'), { version: v, k }) + pause }
    }
    const n = d.release_no ?? ctx.release
    return { state: 'live', text: t(one(k, 'node.done.restart.one', 'node.done.restart.many'), { n: n ?? '—', k }) + pause }
  }
  const code = d.code || codeOf(c.error)
  if (d.failed || code === 'rollback_failed') {
    return { state: 'fail', text: t('node.rollbackFailed', { what: (c.error ?? '').replace(/^rollback_failed:\s*/, '') }) }
  }
  if (d.rolled_back) {
    const step = stepName(d.failed_step || 'verify').toLowerCase()
    return { state: 'warn', text: `${t('node.rolledBack', { step })} ${codeText(code, d.reason)}`.trim() }
  }
  const head = service === 'restart' ? t('node.didNotRun.restart') : t(service === 'logic' ? 'node.didNotRun.logic' : 'node.didNotRun.agent', { name: ctx.name })
  return { state: 'fail', text: code ? `${head} ${codeText(code, c.error)}` : c.error ? `${head} ${c.error}` : head }
}

const STEP_ICON = (s: SwitchStep): { icon: IconName; cls: string; word: string } =>
  s.name === 'rollback' ? { icon: 'state-warn', cls: 'text-warn', word: 'rollback' } : s.ok ? { icon: 'check', cls: 'text-live', word: 'ok' } : { icon: 'state-fail', cls: 'text-fail', word: 'failed' }

/** The steps of a switch: icon and word, name, time in mono. */
export function SwitchSteps({ steps, testId }: { steps?: SwitchStep[]; testId?: string }) {
  if (!steps?.length) return null
  return (
    <ol className="flex flex-col gap-0.5 text-xs" aria-label={t('node.steps')} data-testid={testId}>
      {steps.map((s, i) => {
        const v = STEP_ICON(s)
        return (
          <li key={`${s.name}-${i}`} className="grid grid-cols-[14px_96px_64px_minmax(0,1fr)] items-baseline gap-x-2" data-step={s.name} data-ok={s.ok ? 'true' : 'false'}>
            <span className={`${v.cls} self-center`}>
              <Icon name={v.icon} size={12} label={v.word} />
            </span>
            <span className="text-text-soft">{stepName(s.name)}</span>
            <span className="text-right text-muted">{s.ms !== undefined ? <Num>{s.ms.toLocaleString('en-US')} ms</Num> : '—'}</span>
            <span className="min-w-0 truncate text-muted" title={s.error}>
              {s.error ? codeText(codeOf(s.error), s.error) : ''}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

const OUT_ICON: Record<Outcome['state'], IconName> = { live: 'state-live', warn: 'state-warn', fail: 'state-fail' }
const OUT_CLS: Record<Outcome['state'], string> = { live: 'text-live', warn: 'text-warn', fail: 'text-fail' }

/** The outcome sentence with its state icon, then the steps (history row, fleet table). */
export function SwitchOutcome({ o, steps, testId }: { o: Outcome; steps?: SwitchStep[]; testId?: string }) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId} data-state={o.state}>
      <p className="flex items-start gap-1.5 text-xs text-text-soft">
        <span className={`${OUT_CLS[o.state]} mt-px`}>
          <Icon name={OUT_ICON[o.state]} size={12} />
        </span>
        <span>{o.text}</span>
      </p>
      <SwitchSteps steps={steps} testId={testId ? `${testId}-steps` : undefined} />
    </div>
  )
}

/** Which service a host command is (restart_node, update {service}). */
export function serviceOf(c: Pick<HostCommand, 'kind' | 'args'>): SwitchService | undefined {
  if (c.kind === 'restart_node') return 'restart'
  if (c.kind === 'update') return c.args?.service === 'agent' ? 'agent' : 'logic'
  return undefined
}

/** A switch's outcome as a toast; success fades after 4 s, a rollback or a failure stays until dismissed. */
export function SwitchToast({ o, testId, onClose }: { o: { state: Outcome['state'] | 'idle'; title: ReactNode }; testId: string; onClose: () => void }) {
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    if (o.state !== 'live' && o.state !== 'idle') return
    const id = window.setTimeout(() => close.current(), 4000)
    return () => window.clearTimeout(id)
  }, [o])
  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[70] flex justify-end max-md:inset-x-0">
      <div className="pointer-events-auto w-full max-w-[360px] max-md:max-w-none [&_.sx-toast]:w-full" data-testid={testId}>
        <Toast state={o.state} title={o.title} onClose={onClose} closeLabel={t('common.dismiss')} />
      </div>
    </div>
  )
}

/** The toast and the event of a finished switch on a node. */
export function switchDone(service: SwitchService, c: HostCommand, ctx: { nodeId: string; name: string; release?: number; streams: number; version?: string }) {
  const o = outcome(service, c, ctx)
  const d = c.data ?? {}
  const k = typeof d.streams === 'number' ? d.streams : ctx.streams
  let title: ReactNode = o.text
  if (o.state === 'live') {
    if (service === 'restart') title = tn(k === 1 ? 'node.toast.restart.one' : 'node.toast.restart.many', { name: ctx.name, n: typeof d.release_no === 'number' ? d.release_no : (ctx.release ?? '—'), k })
    else if (service === 'logic') title = tn(k === 1 ? 'node.toast.logic.one' : 'node.toast.logic.many', { name: ctx.name, version: String(d.logic_version || ctx.version || '—'), k })
    else title = tn('node.toast.agent', { name: ctx.name, version: String(d.version || ctx.version || '—') })
    if (typeof d.pause_ms === 'number') title = <>{title} {tn('node.pause', { n: d.pause_ms })}</>
  }
  pushOperatorEvent({ at: new Date().toISOString(), nodeId: ctx.nodeId, name: ctx.name, text: o.text })
  return { state: o.state, title }
}

